import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import BarChart from "../../components/analytics/BarChart"
import SummaryTable from "../../components/analytics/SummaryTable"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import FreeWindowNotice from "../../components/account/FreeWindowNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import type { HashtagSummary, ChartData } from "../../types/analytics"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.hashtagsPeriod"

const METRIC_OPTIONS = [
  { key: "avg_engagement_rate", label: "平均ENG率" },
  { key: "posts_count",         label: "投稿数" },
  { key: "avg_reach",           label: "平均リーチ" },
  { key: "avg_likes",           label: "平均いいね" },
  { key: "avg_comments",        label: "平均コメント" },
  { key: "avg_saves",           label: "平均保存" },
  { key: "avg_shares",          label: "平均シェア" },
]

const SUMMARY_COLUMNS = [
  { key: "posts_count",         label: "投稿数" },
  { key: "avg_reach",           label: "平均リーチ" },
  { key: "avg_likes",           label: "平均いいね" },
  { key: "avg_comments",        label: "平均コメント" },
  { key: "avg_saves",           label: "平均保存" },
  { key: "avg_shares",          label: "平均シェア" },
  { key: "avg_engagement_rate", label: "平均ENG率" },
]

const fmtDateRange = (p: PeriodEntry) =>
  `${p.startDate.replace(/-/g, "/")} 〜 ${p.endDate.replace(/-/g, "/")}`

const HashtagsPage = () => {
  const { selectedAccountId, selectedAccount, setSingleAccountOnly } = useSelectedAccount()
  const navigate = useNavigate()
  const barChartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [dataByPeriod, setDataByPeriod] = useState<HashtagSummary[][]>([])
  const [loading, setLoading]           = useState(true)
  const [metric, setMetric]             = useState("avg_engagement_rate")
  const [periods, setPeriods]           = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))

  const fetchData = useCallback(async () => {
    if (!selectedAccountId) { setLoading(false); return }
    setLoading(true)
    try {
      const results = await Promise.all(
        periods.map(p => {
          const params = new URLSearchParams({
            instagram_account_id: String(selectedAccountId),
            start_date: p.startDate,
            end_date:   p.endDate,
          })
          return api.get<HashtagSummary[]>(`/analytics/hashtags?${params}`)
        })
      )
      setDataByPeriod(results)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId, periods])

  useEffect(() => { fetchData() }, [fetchData])

  const combinedChartData = useMemo((): ChartData => {
    const metricLabel = METRIC_OPTIONS.find(m => m.key === metric)?.label ?? metric
    const val = (h: HashtagSummary) => (h[metric as keyof HashtagSummary] as number) ?? 0
    const mainData = dataByPeriod[0] ?? []
    const mainSorted = [...mainData].sort((a, b) => val(b) - val(a))

    if (dataByPeriod.length <= 1) {
      return {
        labels: mainSorted.map(h => `#${h.tag}`),
        datasets: [{ label: metricLabel, data: mainSorted.map(val), backgroundColor: colors.primary }],
      }
    }

    // 全期間のタグをまとめる（メイン期間の順を優先）
    const allTags = [
      ...mainSorted.map(h => h.tag),
      ...dataByPeriod.slice(1).flatMap(d =>
        (d ?? []).map(h => h.tag).filter(t => !mainSorted.some(m => m.tag === t))
      ),
    ]
    return {
      labels: allTags.map(t => `#${t}`),
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (pi >= periods.length) return []
        const c = getAccountChartColor(pi)
        const map = new Map((data ?? []).map(h => [h.tag, val(h)]))
        return [{
          label: fmtDateRange(periods[pi]),
          data: allTags.map(t => map.get(t) ?? 0),
          backgroundColor: c.bg,
          borderColor: c.border,
          borderWidth: 1,
        }]
      }),
    }
  }, [dataByPeriod, metric, periods])

  const mainData = dataByPeriod[0] ?? []
  const summaryRows = mainData.map(h => ({
    label:  `#${h.tag}`,
    values: {
      posts_count:         h.posts_count,
      avg_reach:           h.avg_reach,
      avg_likes:           h.avg_likes,
      avg_comments:        h.avg_comments,
      avg_saves:           h.avg_saves,
      avg_shares:          h.avg_shares,
      avg_engagement_rate: h.avg_engagement_rate,
    },
  }))

  return (
    <AppLayout pageTitle={PAGE_TITLES["/hashtags"]}>
      <div className="analytics">
        {selectedAccount?.plan === "free" && <FreeWindowNotice days={selectedAccount.features.free_window_days} />}
        {!selectedAccountId ? (
          <AccountSelectionNotice />
        ) : (
          <>
            <div className="analytics__header">
              <PeriodSelectorModal
                periods={periods}
                onChange={setPeriods}
                storageKey={STORAGE_KEY}
                maxDays={selectedAccount?.plan === "free" ? selectedAccount.features.free_window_days : undefined}
              />
            </div>

            <div className="filter-tabs">
              {METRIC_OPTIONS.map(m => (
                <button
                  key={m.key}
                  className={`filter-tabs__item${metric === m.key ? " filter-tabs__item--active" : ""}`}
                  onClick={() => setMetric(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : mainData.length === 0 ? (
              <p style={{ color: colors.gray400 }}>期間内にハッシュタグ付きの投稿がありません</p>
            ) : (
              <>
                {combinedChartData.labels.length > 0 && (
                  <div className="analytics__section">
                    <div className="chart-card">
                      <div className="chart-card__header">
                        <span className="chart-card__title">ハッシュタグ比較</span>
                        <div className="chart-card__actions">
                          <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedChartData), `hashtags_chart_${todayStr()}.csv`)}>↓ CSV</button>
                          <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(barChartRef.current, `hashtags_chart_${todayStr()}.png`)}>↓ PNG</button>
                        </div>
                      </div>
                      <div className="chart-card__canvas-wrap" ref={barChartRef}>
                        <BarChart data={combinedChartData} horizontal />
                      </div>
                    </div>
                  </div>
                )}

                <div className="analytics__section">
                  <SummaryTable
                    rows={summaryRows}
                    columns={SUMMARY_COLUMNS}
                    title="ハッシュタグ別指標"
                    filePrefix="hashtags"
                    onRowClick={i => navigate(`/hashtags/${mainData[i].tag}`)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default HashtagsPage
